import lettering from "./lettering.json";

/**
 * Sets a back name in Graduate as the brand kit does on the mockup (brand/src/wear-mockup.mjs): normal tracking,
 * a horizontal condense measured from the mockup's lettering, and a further squeeze only if a name is wider than
 * the nameplate. The result is one path in back-image pixels, ink left edge at x = 0 and baseline at y = 0, with
 * the glyph positions and condense baked in, so the weight stroke stays even at any display size.
 */
type Glyph = { d: string; advance: number; xMin: number; xMax: number };

const glyphs = lettering.glyphs as Record<string, Glyph>;
const kerning = lettering.kerning as Record<string, number>;
const { sizePx, tracking, condense, strokePx, maxWidthPx } = lettering.setting;
const scale = sizePx / lettering.unitsPerEm;

const r = (n: number) => +n.toFixed(2);

/** Maps every coordinate pair of an absolute M/L/Q/C/Z path. */
function mapPath(d: string, f: (x: number, y: number) => [number, number]) {
  const tokens = d.match(/[MLQCZ]|-?\d*\.?\d+(?:e-?\d+)?/g) ?? [];
  let out = "";
  let pair: number[] = [];
  for (const t of tokens) {
    if (/[MLQCZ]/.test(t)) {
      out += t;
      continue;
    }
    pair.push(Number(t));
    if (pair.length === 2) {
      const [x, y] = f(pair[0]!, pair[1]!);
      out += `${r(x)} ${r(y)} `;
      pair = [];
    }
  }
  return out.trim();
}

export function setBackName(printed: string) {
  const chars = [...printed].filter((c) => glyphs[c]);
  const trackPx = (tracking / 1000) * sizePx;
  let x = 0;
  let left = Infinity;
  let right = -Infinity;
  const placed: { g: Glyph; x: number }[] = [];
  chars.forEach((c, i) => {
    const g = glyphs[c]!;
    if (g.d) {
      placed.push({ g, x });
      left = Math.min(left, x + g.xMin * scale);
      right = Math.max(right, x + g.xMax * scale);
    }
    x += g.advance * scale;
    const next = chars[i + 1];
    if (next) x += (kerning[c + next] ?? 0) * scale + trackPx;
  });
  if (!placed.length) return { d: "", width: 0, strokeWidth: strokePx };

  const condensed = (right - left) * condense;
  const sx = condense * Math.min(1, maxWidthPx / condensed);
  const d = placed
    .map(({ g, x: gx }) =>
      mapPath(g.d, (px, py) => [(gx + px * scale - left) * sx, py * scale]),
    )
    .join("");
  return { d, width: r((right - left) * sx), strokeWidth: strokePx };
}
